import { useState, type FormEvent } from 'react';
import type { Editor } from '@tiptap/react';
import { FiGrid } from 'react-icons/fi';
import { Button } from '@/shared/ui/button';
import { buttonStyles } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from '@/shared/ui/popover';
import { richTextToolbarStyles } from '@/shared/ui/rich-text-editor/styles';
import { toolbarIconClass } from './toolbar-controls';

const DEFAULT_SIZE = '3';
const MIN_SIZE = 1;
const MAX_SIZE = 8;

/** A size the schema will happily render: whole, and within the offered range. */
function tableSize(raw: string): number {
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed)) return MIN_SIZE;
  return Math.min(MAX_SIZE, Math.max(MIN_SIZE, parsed));
}

/**
 * The table starter: choose rows and columns, insert a header-ed grid.
 *
 * The steppers hold the typed text rather than the parsed number, so clearing a
 * field does not snap it to "1" under the operator's cursor; the bounds are
 * applied when the table is inserted and stated in the button's name.
 */
export function TableInsert({ editor, isDisabled }: { editor: Editor; isDisabled: boolean }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(DEFAULT_SIZE);
  const [cols, setCols] = useState(DEFAULT_SIZE);
  const rowCount = tableSize(rows);
  const colCount = tableSize(cols);

  function insert(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    editor
      .chain()
      .focus()
      .insertTable({ rows: rowCount, cols: colCount, withHeaderRow: true })
      .run();
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label="Insert table"
        disabled={isDisabled}
        className={buttonStyles({ variant: 'ghost', size: 'xs' })}
      >
        <FiGrid className={toolbarIconClass} aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="start">
        <PopoverTitle>Insert table</PopoverTitle>
        <form onSubmit={insert} className={richTextToolbarStyles.popoverForm}>
          <Input
            label="Rows"
            type="number"
            min={MIN_SIZE}
            max={MAX_SIZE}
            value={rows}
            onChange={(event) => setRows(event.target.value)}
          />
          <Input
            label="Columns"
            type="number"
            min={MIN_SIZE}
            max={MAX_SIZE}
            value={cols}
            onChange={(event) => setCols(event.target.value)}
          />
          <Button type="submit" size="sm">
            Add {rowCount}x{colCount} table
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
